import type { MapViewerNode } from '@3ddv/dvm-internal';
import {
  POPOVER_MANAGER,
  SeatPopoverComponent,
  SectionPopoverComponent,
  type IPopoverManager,
} from '@3ddv/software-division-components/dvm/popover';
import { Injectable, ViewContainerRef, inject, signal } from '@angular/core';
import { GeneralAvailability } from '@core/models/general-availability.model';
import {
  AvailabilityService,
  DvmService,
  PopoverDataTransformerService,
  TutorialService,
} from '@core/services';
import { ConnectionService } from '@features/pages/select-seat/services/connection.service';
import { BestAvailableService } from './best-available.service';

@Injectable({
  providedIn: 'root',
})
export class Popover3dviewService {
  private dvm = inject(DvmService);
  private availableService = inject(AvailabilityService);
  private bestAvailableService = inject(BestAvailableService);
  private tutorialService = inject(TutorialService);
  private dataTransformer = inject(PopoverDataTransformerService);
  private connectionService = inject(ConnectionService);
  private popoverManager: IPopoverManager = inject(POPOVER_MANAGER);

  popoverTimer: ReturnType<typeof setTimeout>;
  popoverTimerTime = 200; // ms
  currentView: MapViewerNode['id'];
  tooltip: HTMLElement;
  isTicketFeeEnabled = signal(false);

  constructor() {
    this.getIsClientTicketFeeEnabled();
  }

  private getIsClientTicketFeeEnabled() {
    this.connectionService.getCurrentClient().subscribe(response => {
      this.isTicketFeeEnabled.set(!!response.data?.isTicketFeeEnabled);
    });
  }

  setTimer(popoverTimerTime: number = this.popoverTimerTime) {
    // deleting the timer prevents us from coming into component init with a timer asynchronously running
    this.deleteTimer();

    this.popoverTimer = setTimeout(() => {
      this.removeTooltip();
    }, popoverTimerTime);
  }

  deleteTimer() {
    if (this.popoverTimer) {
      clearTimeout(this.popoverTimer);
    }
  }

  removeTooltip() {
    if (this.tutorialService.isOpen()) {
      return;
    }

    this.popoverManager.clearAll();
  }

  public showTooltip(
    node: MapViewerNode,
    tooltip: HTMLElement,
    popover: ViewContainerRef,
    container: HTMLElement
  ) {
    this.deleteTimer();
    this.currentView = node.id;
    this.tooltip = tooltip;

    if (
      (node.type === 'section' || node.type === 'general_admission') &&
      this.dvm.isTopView()
    ) {
      this.createSectionPopover(popover, node, tooltip, container);
    } else if (node.type === 'seat') {
      this.createSeatPopover(popover, node, tooltip, container);
    }
  }

  private createSectionPopover(
    element: ViewContainerRef,
    node: MapViewerNode,
    tooltip: HTMLElement,
    container: HTMLElement
  ): void {
    const tdcId: string = node.id;

    let sectionData: GeneralAvailability;
    if (Object.keys(this.availableService.sectionsOnlyAdas).includes(tdcId)) {
      sectionData = this.availableService.sectionsOnlyAdas[tdcId];
    } else {
      sectionData = this.availableService.sectionsAvailability[tdcId];
    }

    sectionData.mmcID = node.original_id;

    const priceRangeWithFees = this.calculatePriceRangeWithFees(
      sectionData.sectionID,
      sectionData.priceRange
    );

    const transformedData = this.dataTransformer.transformSectionData(
      sectionData,
      node,
      undefined,
      this.bestAvailableService.selectedSectionsForBA?.includes(tdcId) || false,
      priceRangeWithFees
    );

    this.popoverManager.showPopover({
      nodeId: node.id,
      node,
      container: element,
      tooltip,
      containerElement: container,
      component: SectionPopoverComponent,
      componentInputs: {
        data: transformedData,
        showBestAvailable: true,
        showOpenSeatMap: true,
        excludeSeatMapSections: ['S_BLCHADA', 'S_Bleacher'],
        isTicketFeeEnabled: this.isTicketFeeEnabled(),
      },
      eventHandlers: {
        mouseEnter: this.deleteTimer.bind(this),
        mouseLeave: this.setTimer.bind(this),
        load3dView: () => {
          this.removeTooltip();
          this.deleteTimer();
          this.dvm.load3dView(sectionData.sectionID);
        },
        selectForBA: (isSelected: boolean) => {
          if (isSelected) {
            this.bestAvailableService.selectSectionForBA(sectionData.sectionID);
          } else {
            this.bestAvailableService.unselectSectionForBA(
              sectionData.sectionID
            );
          }
        },
        openSeatMap: () => {
          this.dvm.openSeatMap(sectionData.sectionID).subscribe();
          this.setTimer();
        },
      },
    });
  }

  private createSeatPopover(
    element: ViewContainerRef,
    node: MapViewerNode,
    tooltip: HTMLElement,
    container: HTMLElement
  ): void {
    const seatData = this.availableService.getSeatByIdFromAvailability(node.id);
    const transformedData = this.dataTransformer.transformSeatData(
      seatData,
      node,
      undefined
    );

    this.popoverManager.showPopover({
      nodeId: node.id,
      node,
      container: element,
      tooltip,
      containerElement: container,
      component: SeatPopoverComponent,
      componentInputs: {
        data: transformedData,
        showLoader: true,
      },
      eventHandlers: {
        mouseEnter: this.deleteTimer.bind(this),
        mouseLeave: this.setTimer.bind(this),
      },
    });
  }

  private calculatePriceRangeWithFees(
    sectionId: string,
    priceRange: { value: number }[]
  ): string | undefined {
    if (!this.isTicketFeeEnabled() || !priceRange || priceRange.length === 0) {
      return undefined;
    }

    const sectionSeats = this.availableService.seatsAvailability[sectionId];

    if (!sectionSeats) {
      return undefined;
    }

    const seatIds = Object.keys(sectionSeats);
    if (seatIds.length === 0) {
      return undefined;
    }

    const minPriceSeatId = seatIds.find(seatId => {
      const seat = sectionSeats[seatId];
      return seat?.price?.[0]?.value === priceRange[0].value;
    });

    const maxPriceSeatId = seatIds.find(seatId => {
      const seat = sectionSeats[seatId];
      return seat?.price?.[0]?.value === priceRange[1].value;
    });

    const minSeat = minPriceSeatId ? sectionSeats[minPriceSeatId] : null;
    const maxSeat = maxPriceSeatId ? sectionSeats[maxPriceSeatId] : null;

    if (!minSeat || !maxSeat) {
      return undefined;
    }

    const minPriceWithFees = minSeat.price[0].totalPriceWithFees;
    const maxPriceWithFees = maxSeat.price[0].totalPriceWithFees;

    const formattedMinPriceWithFees = Number.isInteger(minPriceWithFees)
      ? minPriceWithFees.toString()
      : minPriceWithFees.toFixed(2);
    const formattedMaxPriceWithFees = Number.isInteger(maxPriceWithFees)
      ? maxPriceWithFees.toString()
      : maxPriceWithFees.toFixed(2);

    if (minPriceWithFees === maxPriceWithFees) {
      return formattedMinPriceWithFees;
    }

    return `${formattedMinPriceWithFees} - ${formattedMaxPriceWithFees}`;
  }
}
